import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAdminApi } from '../../hooks/useAdminApi';
import AdminLayout from '../../components/admin/AdminLayout';
import EditTenantModal from '../../components/admin/EditTenantModal';
import ProvisionTenantModal from '../../components/admin/ProvisionTenantModal';
import GenerateLicenseModal from '../../components/admin/GenerateLicenseModal';
import {
  ArrowLeft,
  Building2,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Users,
  Key,
  CreditCard,
  Edit,
  Server,
  ExternalLink,
  RefreshCw,
  CheckCircle,
  Clock,
  XCircle,
  AlertTriangle,
  Plus,
  Globe,
  Activity
} from 'lucide-react';

const TenantDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { get, loading } = useAdminApi();

  const [tenant, setTenant] = useState(null);
  const [licenses, setLicenses] = useState([]);
  const [payments, setPayments] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  // Modals
  const [editModal, setEditModal] = useState(false);
  const [provisionModal, setProvisionModal] = useState(false);
  const [generateLicenseModal, setGenerateLicenseModal] = useState(false);

  useEffect(() => {
    fetchTenantDetails();
  }, [id]);

  const fetchTenantDetails = async () => {
    setRefreshing(true);
    try {
      // Fetch tenant details
      const tenantRes = await get(`/tenants/${id}`);
      if (tenantRes.success) {
        setTenant(tenantRes.data || tenantRes.tenant);
      }

      // Fetch tenant's licenses
      const licensesRes = await get(`/licenses?tenant=${id}`);
      if (licensesRes.success) {
        setLicenses(licensesRes.data || licensesRes.licenses || []);
      }

      // Fetch tenant's payments
      const paymentsRes = await get(`/payments?tenant=${id}`);
      if (paymentsRes.success) {
        setPayments(paymentsRes.data || paymentsRes.payments || []);
      }
    } catch (error) {
      console.error('Failed to fetch tenant details:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const handleModalClose = (refreshData = false) => {
    setEditModal(false);
    setProvisionModal(false);
    setGenerateLicenseModal(false);
    if (refreshData) {
      fetchTenantDetails();
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-700',
      pending: 'bg-yellow-100 text-yellow-700',
      suspended: 'bg-red-100 text-red-700',
      expired: 'bg-gray-100 text-gray-700'
    };
    const icons = {
      active: CheckCircle,
      pending: Clock,
      suspended: XCircle,
      expired: AlertTriangle
    };
    const Icon = icons[status] || Clock;
    return (
      <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${styles[status] || styles.pending}`}>
        <Icon className="w-4 h-4 mr-1.5" />
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  const getPlanBadge = (plan) => {
    const styles = {
      starter: 'bg-blue-100 text-blue-700',
      professional: 'bg-purple-100 text-purple-700',
      enterprise: 'bg-pink-100 text-pink-700'
    };
    return (
      <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${styles[plan] || styles.starter}`}>
        {plan?.charAt(0).toUpperCase() + plan?.slice(1)}
      </span>
    );
  };

  if (loading && !tenant) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  if (!tenant) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h2 className="text-lg font-medium text-gray-900">Tenant not found</h2>
          <p className="text-gray-500 mt-1">The tenant you're looking for doesn't exist.</p>
          <Link
            to="/admin/tenants"
            className="inline-flex items-center mt-4 text-purple-600 hover:text-purple-700 font-medium"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Tenants
          </Link>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/admin/tenants')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors mr-4"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div className="flex items-center">
              <div className="w-14 h-14 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                <Building2 className="w-7 h-7 text-white" />
              </div>
              <div className="ml-4">
                <h1 className="text-2xl font-bold text-gray-900">{tenant.companyName}</h1>
                <p className="text-gray-500">
                  {tenant.domain?.type === 'custom' && tenant.domain?.customDomain
                    ? tenant.domain.customDomain
                    : `${tenant.domain?.subdomain || tenant.subdomain}.bestohrms.com`}
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchTenantDetails}
              disabled={refreshing}
              className="flex items-center px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={() => setEditModal(true)}
              className="flex items-center px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </button>
            <button
              onClick={() => setProvisionModal(true)}
              className="flex items-center px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-colors"
            >
              <Server className="w-4 h-4 mr-2" />
              Provision
            </button>
          </div>
        </div>

        {/* Status & Plan Badges */}
        <div className="flex items-center gap-4">
          {getStatusBadge(tenant.status)}
          {getPlanBadge(tenant.plan)}
          {tenant.provisioned && (
            <span className="inline-flex items-center px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">
              <Server className="w-4 h-4 mr-1.5" />
              Provisioned
            </span>
          )}
        </div>

        {/* Info Cards Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Company Info */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Company Information</h3>
            <div className="space-y-4">
              <div className="flex items-start">
                <Building2 className="w-5 h-5 text-gray-400 mt-0.5 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">Company Name</p>
                  <p className="font-medium text-gray-900">{tenant.companyName}</p>
                </div>
              </div>
              <div className="flex items-start">
                <Users className="w-5 h-5 text-gray-400 mt-0.5 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">Contact Person</p>
                  <p className="font-medium text-gray-900">{tenant.contactPerson}</p>
                </div>
              </div>
              <div className="flex items-start">
                <Mail className="w-5 h-5 text-gray-400 mt-0.5 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium text-gray-900">{tenant.email}</p>
                </div>
              </div>
              <div className="flex items-start">
                <Phone className="w-5 h-5 text-gray-400 mt-0.5 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <p className="font-medium text-gray-900">{tenant.phone || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-start">
                <MapPin className="w-5 h-5 text-gray-400 mt-0.5 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">Address</p>
                  <p className="font-medium text-gray-900">{tenant.address || 'N/A'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Subscription Info */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Subscription Details</h3>
            <div className="space-y-4">
              {/* Custom Domain */}
              {tenant.domain?.type === 'custom' && tenant.domain?.customDomain && (
                <div className="flex items-start">
                  <Globe className="w-5 h-5 text-purple-500 mt-0.5 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">Custom Domain</p>
                    <div className="flex items-center gap-2">
                      <a
                        href={`https://${tenant.domain.customDomain}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-purple-600 hover:text-purple-700 inline-flex items-center"
                      >
                        {tenant.domain.customDomain}
                        <ExternalLink className="w-4 h-4 ml-1" />
                      </a>
                      {!tenant.domain.isConfigured && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          Pending Setup
                        </span>
                      )}
                      {tenant.domain.isConfigured && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Configured
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Subdomain */}
              <div className="flex items-start">
                <Globe className="w-5 h-5 text-gray-400 mt-0.5 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">
                    {tenant.domain?.type === 'custom' ? 'Fallback Subdomain' : 'Subdomain'}
                  </p>
                  <a
                    href={`https://${tenant.domain?.subdomain || tenant.subdomain}.bestohrms.com`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-purple-600 hover:text-purple-700 inline-flex items-center"
                  >
                    {tenant.domain?.subdomain || tenant.subdomain}.bestohrms.com
                    <ExternalLink className="w-4 h-4 ml-1" />
                  </a>
                </div>
              </div>
              <div className="flex items-start">
                <Key className="w-5 h-5 text-gray-400 mt-0.5 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">Active License</p>
                  <p className="font-medium text-gray-900">
                    {licenses.find(l => l.status === 'active')?.licenseKey?.substring(0, 20) || 'No active license'}...
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <Activity className="w-5 h-5 text-gray-400 mt-0.5 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">Employee Count</p>
                  <p className="font-medium text-gray-900">{tenant.employeeCount || 0}</p>
                </div>
              </div>
              <div className="flex items-start">
                <Calendar className="w-5 h-5 text-gray-400 mt-0.5 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">Registered On</p>
                  <p className="font-medium text-gray-900">{formatDate(tenant.createdAt)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Quick Stats</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-purple-50 rounded-xl p-4 text-center">
                <Key className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-purple-700">{licenses.length}</p>
                <p className="text-sm text-purple-600">Licenses</p>
              </div>
              <div className="bg-green-50 rounded-xl p-4 text-center">
                <CreditCard className="w-6 h-6 text-green-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-green-700">{payments.length}</p>
                <p className="text-sm text-green-600">Payments</p>
              </div>
              <div className="col-span-2 bg-blue-50 rounded-xl p-4 text-center">
                <p className="text-sm text-blue-600 mb-1">Total Revenue</p>
                <p className="text-2xl font-bold text-blue-700">
                  {formatCurrency(payments.reduce((sum, p) => sum + (p.amount || 0), 0))}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Licenses Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Licenses</h3>
            <button
              onClick={() => setGenerateLicenseModal(true)}
              className="flex items-center px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm font-medium"
            >
              <Plus className="w-4 h-4 mr-2" />
              Generate License
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">License Key</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Plan</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Expires</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {licenses.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                      No licenses found
                    </td>
                  </tr>
                ) : (
                  licenses.map((license) => (
                    <tr key={license._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-mono text-sm text-gray-900">
                        {license.licenseKey?.substring(0, 30)}...
                      </td>
                      <td className="px-6 py-4">
                        {getPlanBadge(license.plan)}
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(license.status)}
                      </td>
                      <td className="px-6 py-4 text-gray-500">
                        {formatDate(license.expiresAt)}
                      </td>
                      <td className="px-6 py-4 text-gray-500">
                        {formatDate(license.createdAt)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Payments Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Payment History</h3>
            <Link
              to={`/admin/payments?tenant=${tenant._id}`}
              className="text-sm text-purple-600 hover:text-purple-700 font-medium"
            >
              View all
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Transaction ID</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Plan</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {payments.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                      No payments found
                    </td>
                  </tr>
                ) : (
                  payments.slice(0, 5).map((payment) => (
                    <tr key={payment._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-mono text-sm text-gray-900">
                        {payment.transactionId || payment._id?.substring(0, 12)}
                      </td>
                      <td className="px-6 py-4 font-medium text-gray-900">
                        {formatCurrency(payment.amount)}
                      </td>
                      <td className="px-6 py-4 capitalize text-gray-500">
                        {payment.plan}
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(payment.status)}
                      </td>
                      <td className="px-6 py-4 text-gray-500">
                        {formatDate(payment.createdAt)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modals */}
      {editModal && (
        <EditTenantModal
          tenant={tenant}
          onClose={() => handleModalClose(false)}
          onSave={() => handleModalClose(true)}
        />
      )}

      {provisionModal && (
        <ProvisionTenantModal
          tenant={tenant}
          onClose={() => handleModalClose(false)}
          onSave={() => handleModalClose(true)}
        />
      )}

      {generateLicenseModal && (
        <GenerateLicenseModal
          tenant={tenant}
          onClose={() => handleModalClose(false)}
          onSave={() => handleModalClose(true)}
        />
      )}
    </AdminLayout>
  );
};

export default TenantDetailsPage;
